import {channel_fetchContainerByOwner_Api} from 'apis'
import {getId, getType} from 'apis/model/base'
import {LoginContext} from 'components/LoginContext'
import useAsyncAction from 'modules/asyncCache/useAsyncAction'
import _ from 'lodash'
import React, {createContext, useCallback, useContext, useEffect, useMemo, useRef, useState,} from 'react'

export const PostFormContext = createContext({
    options: [],
    current: null,
    setCurrent: () => {
    },
    isLoading: false,
    keyword: '',
    setKeyword: () => {
    },
    scopeId: null,
    scopeType: null,
})

// ─── Helpers ──────────────────────────────────────────────────────────────────

export const buildChannelOption = (ch) => ({
    value: getId(ch),
    id: getId(ch),
    type: getType(ch),
    label: ch?.title ?? ch?.name ?? ch?.idname ?? '',
    data: ch,
    creators: ch?.creators ?? [],
    owner: ch?.owner ?? null,
    is_default: ch?.is_default ?? 0,
})

export const resolveDefaultCreator = (channel, login) => {
    const creators = channel?.creators ?? []
    if (creators.length > 0) {
        const first = creators[0]
        return first?.owner ?? first ?? null
    }
    return login ?? null
}

const buildCurrentFromChannel = (channel, login) => ({
    container: channel,
    creator: resolveDefaultCreator(channel, login),
    privacy: 1,
})

// ─── Provider ─────────────────────────────────────────────────────────────────

export const PostFormProvider = ({container, defaultChannel, initialCurrent, children}) => {
    const login = useContext(LoginContext)

    const scopeId = getId(container) ?? container?.id
    const scopeType = getType(container) ?? container?.type

    // Keep defaultChannel in a ref AND reactive — used for both
    // immediate init and re-init when prop changes (e.g. org data loads async)
    const defaultChannelRef = useRef(defaultChannel)
    useEffect(() => {
        defaultChannelRef.current = defaultChannel
    }, [getId(defaultChannel)])

    // ── Channel list ───────────────────────────────────────────────────────
    const [channelItems, setChannelItems] = useState(() =>
        defaultChannel ? [defaultChannel] : []
    )

    const [keyword, setKeywordState] = useState('')

    const {isLoading, handleAsyncAction: fetchChannels} = useAsyncAction({
        apiInfo: channel_fetchContainerByOwner_Api,
        onSuccess: (result, data) => {
            const items = _.get(data, 'response.data.data', [])
            if (Array.isArray(items) && items.length > 0) {
                setChannelItems(items)
            }
        },
        onError: () => {
        },
    })

    const debounceTimer = useRef(null)

    const doFetch = useCallback(
        (kw = '') => {
            !!login?.id && fetchChannels({
                ...(scopeId && scopeType ? {id: scopeId, type: scopeType} : {}),
                keyword: kw,
                ...(defaultChannelRef.current
                    ? {with_channel_id: getId(defaultChannelRef.current)}
                    : {}),
            })
        },
        [scopeId, scopeType]
    )

    useEffect(() => {
        doFetch('')
    }, [scopeId, scopeType])

    const handleKeyword = useCallback(
        (kw) => {
            setKeywordState(kw)
            clearTimeout(debounceTimer.current)
            debounceTimer.current = setTimeout(() => doFetch(kw), 350)
        },
        [doFetch]
    )

    // ── Options ────────────────────────────────────────────────────────────
    const options = useMemo(() => {
        const dc = defaultChannelRef.current
        const allItems =
            dc && !channelItems.find((c) => getId(c) === getId(dc))
                ? [dc, ...channelItems]
                : channelItems
        return allItems.map(buildChannelOption)
    }, [channelItems])

    // ── Current selection ──────────────────────────────────────────────────
    // Priority: initialCurrent (edit mode) > defaultChannel > fetch fallback
    const [current, setCurrentState] = useState(() => {
        if (initialCurrent) return initialCurrent
        if (defaultChannel) return buildCurrentFromChannel(defaultChannel, login)
        return null
    })

    // Reactive: nếu initialCurrent được truyền vào (edit mode),
    // KHÔNG để các effect bên dưới override nó
    const isControlled = Boolean(initialCurrent)

    // Reactive: if defaultChannel prop arrives late (async org load) and
    // current is still null — set it immediately without waiting for fetch
    useEffect(() => {
        if (isControlled) return  // edit mode — không override
        if (!defaultChannel) return
        if (current !== null) return
        setCurrentState(buildCurrentFromChannel(defaultChannel, login))
    }, [getId(defaultChannel)])

    // Fallback: after fetch, if still no current — pick is_default or first
    useEffect(() => {
        if (isControlled) return  // edit mode — không override
        if (current !== null) return
        if (options.length === 0) return
        const defaultOpt = options.find((o) => o.is_default === 1) ?? options[0]
        setCurrentState(buildCurrentFromChannel(defaultOpt.data, login))
    }, [options])

    // Public setCurrent — called from PostSettingForm confirm OR
    // PostScreenComponent close (passes null to reset).
    // On reset (null): restore to defaultChannel instead of staying null,
    // so reopening always shows a valid selection.
    const setCurrent = useCallback(
        (next) => {
            if (next === null) {
                // Restore default instead of clearing — fixes "reopen = empty" bug
                const dc = defaultChannelRef.current
                if (dc) {
                    setCurrentState(buildCurrentFromChannel(dc, login))
                } else if (options.length > 0) {
                    const defaultOpt = options.find((o) => o.is_default === 1) ?? options[0]
                    setCurrentState(buildCurrentFromChannel(defaultOpt.data, login))
                } else {
                    setCurrentState(null)
                }
            } else {
                setCurrentState(next)
            }
        },
        [options, login]
    )

    // ── Context value ──────────────────────────────────────────────────────
    const value = useMemo(
        () => ({
            options,
            current,
            setCurrent,
            isLoading,
            keyword,
            setKeyword: handleKeyword,
            scopeId,
            scopeType,
        }),
        [options, current, isLoading, keyword, scopeId, scopeType]
    )

    return (
        <PostFormContext.Provider value={value}>
            {children}
        </PostFormContext.Provider>
    )
}

export const usePostFormContext = () => useContext(PostFormContext)

export default PostFormContext